import React, { useState, useEffect, useCallback } from 'react';
// import { useParams } from 'react-router-dom'; // No longer using useParams for tripOrderId
import SearchForm from './SearchForm';
import TripCollection from './TripCollection';
import Matrix from './Matrix'; // Import the Matrix component
import API_HOST from '../config';
import PriceRevalidationModal from './PriceRevalidationModal';
import GoogleFlights from './GoogleFlights';

function TripOrder() {
  const renderSafe = (value) => {
    if (value === null || value === undefined) {
      return ''; // Render empty string for null/undefined
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value); // Use JSON.stringify for detailed object view
      } catch (e) {
        return '[Unserializable Object]';
      }
    }
    return String(value); // Explicitly convert everything else to a string
  };

  const formatTimeToHHMM = (timeString) => {
    if (!timeString || typeof timeString !== 'string') {
      return 'N/A'; // Or an empty string, depending on desired fallback
    }
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeString; // Return original if not in expected HH:mm:ss or HH:mm format
  };

  // const { tripOrderId } = useParams(); // Replaced by hash logic
  const [tripOrder, setTripOrder] = useState({
    id: null, // This id will be set by refreshTripOrder or handleTotalSearch
    created_by: '',
    created_at: '',
    travel_route: '',
    user_request: {
      legs: [],
      adultCount: 0,
      childCount: 0,
      infantCount: 0,
      flightRequestId: ''
    },
    trips: []
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedOptionId, setCopiedOptionId] = useState(null); // For copy feedback
  const [activeTabs, setActiveTabs] = useState({}); // State for active tabs per group
  const [matrixFilters, setMatrixFilters] = useState({ airlines: null, stops: null }); // State for matrix filters
  const [isResultReadyForMatrix, setIsResultReadyForMatrix] = useState(false);
  const [showSelfTransfer, setShowSelfTransfer] = useState(true); // State for show_self_transfer filter
  const [showExtraLeg, setShowExtraLeg] = useState(true); // State for show_extra_leg filter
  const [isRevalidationModalOpen, setIsRevalidationModalOpen] = useState(false);
  const [revalidationData, setRevalidationData] = useState(null);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [revalidatingGroupId, setRevalidatingGroupId] = useState(null);
  const [googleFlights, setGoogleFlights] = useState([]);
  const [isGoogleFlightsLoading, setIsGoogleFlightsLoading] = useState(false);

  useEffect(() => {
    console.log('Initial tripOrder state:', tripOrder);
    setTripOrder(prevState => {
      const newState = {
        ...prevState,
        trips: Array.isArray(prevState.trips) ? prevState.trips : []
      };
      console.log('Updated tripOrder state:', newState);
      return newState;
    });
  }, []);

  // useEffect to fetch data based on URL hash on initial mount
  useEffect(() => {
    const hashValue = window.location.hash;
    let idFromHash = null;

    if (hashValue && hashValue.startsWith('#')) {
      idFromHash = hashValue.substring(1);
    }

    if (idFromHash) {
      console.log(`[TripOrder useEffect] ID from hash (${idFromHash}) found in URL. Calling refreshTripOrder.`);
      // Call refreshTripOrder directly to leverage its search_form handling
      // This will also set tripOrder.id in the state via setTripOrder inside refreshTripOrder
      refreshTripOrder(idFromHash, Date.now(), 0);
    } else {
      // No ID in hash, initialize with empty trips or default state
      console.log('[TripOrder useEffect] No ID in URL hash. Initializing with empty trips.');
      setTripOrder(prevState => ({
        ...prevState,
        id: null, // Ensure ID is null
        user_request: {
          legs: [],
          adultCount: 1,
          childCount: 0,
          infantCount: 0,
          flightRequestId: ''
        },
        trips: []
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount to check initial hash

  // Effect to initialize active tabs when trips change
  useEffect(() => {
    if (tripOrder.trips && Array.isArray(tripOrder.trips)) {
      setActiveTabs(prevTabs => {
        const newTabs = { ...prevTabs };
        tripOrder.trips.forEach((group, groupIndex) => {
          const groupId = group.Id || `group-${groupIndex}`;
          if (!newTabs[groupId]) {
            newTabs[groupId] = "All Flights"; // Default to "All Flights"
          }
        });
        return newTabs;
      });
    }
  }, [tripOrder.trips]);

  const addNewTrip = async () => {
    if (!tripOrder.id) {
      console.error('Cannot add new trip: TripOrder ID is missing');
      return;
    }

    try {
      const response = await fetch(`${API_HOST}/api/trip/trip`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: tripOrder.id,
          position: tripOrder.trips.length + 1,
          description: "Custom Trip"
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newTrip = await response.json();
      console.log('New trip added:', newTrip);

      setTripOrder(prevState => ({
        ...prevState,
        trips: [...prevState.trips, newTrip]
      }));

      // Refresh the entire TripOrder to ensure we have the latest data
      if (tripOrder.id) {
        await refreshTripOrder(tripOrder.id, Date.now(), 0);
      } else {
        console.error("Cannot refresh trip order: ID is missing after adding new trip.");
      }
    } catch (error) {
      console.error('Error adding new trip:', error);
    }
  };

  console.log('Rendering TripOrder. Current state:', tripOrder);

  const fetchGoogleFlights = async (from, to, startDate, endDate) => {
    setIsGoogleFlightsLoading(true);
    setGoogleFlights([]);

    try {
      const params = new URLSearchParams({
        departure_id: from,
        arrival_id: to,
        outbound_date: startDate,
        return_date: endDate,
        limit: 1
      });

      const response = await fetch(`${API_HOST}/api/google_flights/search?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Google Flights API error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Google Flights API response:', data);

      if (data.results && Array.isArray(data.results)) {
        setGoogleFlights(data.results);
      }
    } catch (error) {
      console.error('Error fetching Google Flights:', error);
    } finally {
      setIsGoogleFlightsLoading(false);
    }
  };

  const handleTotalSearch = async (currentLegs) => {
    setIsLoading(true); // Show loader
    console.log('handleTotalSearch called with currentLegs:', currentLegs);

    if (!currentLegs || currentLegs.length === 0) {
      console.error('No currentLegs data available for total search.');
      return;
    }
    if (!tripOrder.user_request) {
      console.error('User request data is not available for passenger counts.');
      return;
    }

    // Trigger Google Flights search in parallel
    if (currentLegs.length >= 2) {
      const outboundLeg = currentLegs[0];
      const returnLeg = currentLegs[1];

      // Fetch Google Flights in parallel (no await here)
      fetchGoogleFlights(
        outboundLeg.departureAirportCode,
        outboundLeg.arrivalAirportCode,
        outboundLeg.departureDate,
        returnLeg.departureDate
      );
    }

    // Construct the segments array by iterating over currentLegs (passed as argument)
    const segments = currentLegs.map(leg => ({
      departure_airport_code: leg.departureAirportCode,
      arrival_airport_code: leg.arrivalAirportCode,
      departure_date: leg.departureDate,
      booking_class: (leg.bookingClass && leg.bookingClass.trim() !== "")
                       ? leg.bookingClass.toUpperCase()
                       : "ECONOMY",
      flexibility: "STRICT" // Default value as specified
    }));

    const requestBody = {
      adult: 1, // Hardcoded as per new requirement
      child: 0, // Hardcoded as per new requirement
      infant: 0, // Hardcoded as per new requirement
      itinerary_limit: 200, // Assuming this default is fine
      segments: segments,
      private_fares_only: false,
      hubs: []
      // Fields explicitly excluded: api_pcc, pcc, preferred_airline, force, flightRequestId, trip_order_id
    };

    console.log('Sending total search request with NEW body:', requestBody);

    try {
      const response = await fetch(`${API_HOST}/radar_trip/new_search_async`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json(); // Attempt to get more error info
        console.error('Total search API error response:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Total search API success response:', result);

      if (result.id) {
        console.log(`New Trip Order ID received: ${result.id}. Updating URL, state, and clearing filters.`);
        window.location.hash = result.id;

        // Batch state updates for ID and filters together.
        // This will trigger the useEffect for refreshing just once with the new ID and cleared filters.
        setIsResultReadyForMatrix(false); // Reset matrix readiness on new search
        setMatrixFilters({ airlines: null, stops: null });
        setTripOrder(prevState => ({
          ...prevState,
          id: result.id,
          user_request: {
            ...prevState.user_request,
            legs: []
          },
          trips: []
        }));
      } else {
        console.error('Total search API success response did not contain a trip order ID (result.id). Polling will not start for new order.');
        setIsLoading(false); // Also hide loader here if API call succeeded but no ID was returned
      }
    } catch (error) {
      console.error('Error sending total search request:', error);
      setIsLoading(false); // Hide loader on error
    }
  };

  const refreshTripOrder = useCallback(async (orderIdToPoll, pollingStartTime, consecutive204Count) => {
    const idToFetch = orderIdToPoll || tripOrder.id;
    console.log(`refreshTripOrder called. orderIdToPoll: ${orderIdToPoll}, pollingStartTime: ${pollingStartTime}, consecutive204Count: ${consecutive204Count}, idToFetch: ${idToFetch}`);

    if (idToFetch) {
      setIsLoading(true); // Ensure loader is active if we are attempting to fetch/poll
    }

    if (Date.now() - pollingStartTime > 120000) { // 120000ms = 2 minutes
      console.log(`Polling timed out after 120 seconds for order ID ${idToFetch}.`);
      setIsLoading(false);
      return;
    }

    if (idToFetch) {
      try {
        let apiUrl = `${API_HOST}/radar_trip/result/${idToFetch}`;
        const queryParams = [];
        if (matrixFilters.airlines) {
          queryParams.push(`airlines=${encodeURIComponent(matrixFilters.airlines)}`);
        }
        if (matrixFilters.stops) {
          queryParams.push(`stops=${encodeURIComponent(matrixFilters.stops)}`);
        }
        // Add show_self_transfer and show_extra_leg parameters
        queryParams.push(`show_self_transfer=${showSelfTransfer}`);
        queryParams.push(`show_extra_leg=${showExtraLeg}`);

        if (queryParams.length > 0) {
          apiUrl += `?${queryParams.join('&')}`;
        }
        console.log("Fetching from URL:", apiUrl); // Log the URL

        const response = await fetch(apiUrl);

        if (response.status === 204) {
          console.log(`Received 204 No Content for order ID ${idToFetch} with filters, will retry in 1 second. Consecutive 204s: ${consecutive204Count + 1}`);
          setTimeout(() => refreshTripOrder(idToFetch, pollingStartTime, consecutive204Count + 1), 1000);
          return;
        }

        // If we receive a 200 OK, we can signal that the matrix can be loaded.
        setIsResultReadyForMatrix(true);

        const data = await response.json();
        // Reset consecutive204Count here implicitly by not carrying it forward unless it's a 204 response.
        // The next call for shouldContinuePolling will pass 0.
        const { search_form, flight_option_groups, ...restOfData } = data;

        const newTrips = Array.isArray(flight_option_groups) ? flight_option_groups : [];

        setTripOrder(prevState => {
            // Ensure idToFetch (the ID we polled with) is consistent with the data's ID if possible,
            // or prioritize data.id if it exists.
            const currentIdInState = prevState.id;
            const responseId = restOfData.id || idToFetch; // Use ID from data if available

            // Only update if the incoming data is for the ID we are currently focused on,
            // or if the state's ID is null (initial load).
            // This helps prevent race conditions where a late response from an old poll
            // overwrites the state for a newer poll.
            if (currentIdInState === null || currentIdInState === responseId || currentIdInState === idToFetch) {
                let updatedUserRequest = prevState.user_request;
                if (search_form) {
                    const newLegs = Array.isArray(search_form.segments) ? search_form.segments.map(segment => ({
                        departureAirportCode: segment.departure_airport_code,
                        arrivalAirportCode: segment.arrival_airport_code,
                        departureDate: segment.departure_date,
                        bookingClass: segment.booking_class
                    })) : prevState.user_request.legs; // keep old legs if new ones are not there

                    updatedUserRequest = {
                        ...prevState.user_request,
                        ...search_form, // spread all fields from search_form
                        legs: newLegs   // override with transformed legs
                    };
                    delete updatedUserRequest.segments; // clean up if segments was spread from search_form
                }

                return {
                    ...prevState,
                    ...restOfData, // other top-level fields from API (like id, created_at, travel_route)
                    id: responseId, // Explicitly set the ID from the response or the polled ID
                    user_request: updatedUserRequest,
                    trips: newTrips // use flight_option_groups as trips
                };
            } else {
                // Data is for a different ID than current state, likely an old response.
                console.warn(`Received data for order ID ${responseId} but current state is for ${currentIdInState}. Discarding stale data.`);
                return prevState; // Return previous state unchanged
            }
        });

        // Define states that indicate a trip part has reached a final state.
        // These are examples; actual states might vary based on system design.
        // TODO: Confirm the complete list of final states from backend documentation or API contract.
        const finalPartStates = [
          "COMPLETED",
          "FLIGHT_FOUND",
          "NO_FLIGHTS_FOUND",
          "ERROR",
          "CANCELLED",
          "FAILED",
          "FILLED",      // Added "FILLED"
          "NOT_FOUND"    // Added "NOT_FOUND"
        ];

        // Check if any trip part is still in a non-final state, using the new 'newTrips' structure.
        // Polling should continue if at least one part is not yet in a final state.
        // This assumes each 'trip' in 'newTrips' (derived from flight_option_groups)
        // has a 'parts' array, and each 'part' has a 'status'.
        const shouldContinuePolling = newTrips.some(trip =>
          trip.parts && Array.isArray(trip.parts) &&
          trip.parts.some(part => !finalPartStates.includes(part.status))
        );

        if (shouldContinuePolling) {
          // Reset consecutive204Count to 0 as we received a valid (non-204) response
          console.log(`Partial results received for order ID ${idToFetch}, will refresh in 2 seconds.`);
          setTimeout(() => refreshTripOrder(idToFetch, pollingStartTime, 0), 2000);
        } else {
          console.log(`All parts final for order ID ${idToFetch}, polling complete.`);
          setIsLoading(false); // Hide loader when polling stops successfully
        }
      } catch (error) {
        console.error(`Error refreshing TripOrder for ID ${idToFetch}:`, error);
        setIsLoading(false); // Hide loader on error
      }
    } else {
      console.log('refreshTripOrder: No ID to fetch (idToFetch is falsy).');
      setIsLoading(false); // Hide loader if no ID to fetch
    }
  }, [tripOrder.id, setIsLoading, setTripOrder, matrixFilters, showSelfTransfer, showExtraLeg]); // Added filter dependencies

  const handleApplyMatrixFilter = (filterType, value) => {
    console.log(`Applying matrix filter: ${filterType} = ${value}`);
    setMatrixFilters(prevFilters => {
      const newFilters = { ...prevFilters };
      if (filterType === 'airlines') {
        newFilters.airlines = value === prevFilters.airlines ? null : value; // Toggle or set new
        newFilters.stops = null; // Clear stops filter when airline is selected
      } else if (filterType === 'stops') {
        newFilters.stops = value === prevFilters.stops ? null : value; // Toggle or set new
        newFilters.airlines = null; // Clear airline filter when stops is selected
      } else if (filterType === 'combined') {
        // For combined, value is an object { airlines, stops }
        // If current filters are same as new, toggle both (set to null)
        // Otherwise, apply new filters
        if (prevFilters.airlines === value.airlines && prevFilters.stops === value.stops) {
          newFilters.airlines = null;
          newFilters.stops = null;
        } else {
          newFilters.airlines = value.airlines;
          newFilters.stops = value.stops;
        }
      } else if (filterType === 'reset') {
        newFilters.airlines = null;
        newFilters.stops = null;
      }
      return newFilters;
    });
  };

  // useEffect to trigger refresh when matrixFilters, showSelfTransfer, showExtraLeg, or tripOrder.id change
  useEffect(() => {
    if (tripOrder.id) { // Only refresh if there's an active tripOrder
      console.log('Filters or tripOrder.id changed, refreshing trip order. matrixFilters:', matrixFilters, 'showSelfTransfer:', showSelfTransfer, 'showExtraLeg:', showExtraLeg);
      // Call refreshTripOrder with current ID, new start time, and reset 204 count
      refreshTripOrder(tripOrder.id, Date.now(), 0);
    }
    // Intentionally not including refreshTripOrder in deps to avoid loop,
    // as it's a useCallback that depends on state that it also sets.
    // The logic within handles the polling lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixFilters, showSelfTransfer, showExtraLeg, tripOrder.id]);

  // useEffect for handling hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hashValue = window.location.hash;
      const idFromHash = hashValue.startsWith('#') ? hashValue.substring(1) : null;

      if (idFromHash) {
        console.log(`[TripOrder hashchange] Hash changed to: ${idFromHash}. Calling refreshTripOrder.`);
        refreshTripOrder(0, idFromHash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    console.log('[TripOrder] Added hashchange event listener.');

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      console.log('[TripOrder] Removed hashchange event listener.');
    };
  }, [refreshTripOrder]); // Dependency: refreshTripOrder ensures the latest version is used

  // Сбрасываем счетчик обновлений при каждом вызове refreshTripOrder из дочерних компонентов
  const resetRefreshCounter = useCallback(() => {
    setRefreshCounter(0);
    // Ensure tripOrder.id is valid before calling.
    if (tripOrder.id) {
      refreshTripOrder(tripOrder.id, Date.now(), 0);
    } else {
      console.warn("resetRefreshCounter called but tripOrder.id is not set.");
    }
  }, [refreshTripOrder, tripOrder.id]);

  const handleTabClick = (groupId, tabName) => {
    setActiveTabs(prevTabs => ({
      ...prevTabs,
      [groupId]: tabName
    }));
  };

  const handleCopyBooking = async (option, groupIndex, optionIndex) => {
    const feedbackId = option.Id || `gen_${groupIndex}_${optionIndex}`;

    // 1. Clipboard API Check (moved to the very beginning)
    if (!navigator.clipboard) {
      console.error('Clipboard API not available. Ensure page is served over HTTPS and context is secure.');
      setCopiedOptionId(feedbackId + '_clipboard_unavailable');
      setTimeout(() => setCopiedOptionId(null), 3000);
      return;
    }

    console.log('handleCopyBooking: option object for feedbackId', feedbackId, ':', JSON.stringify(option, null, 2));

    // 2. Select PriceOption
    const selectedPriceOpt = (option.PriceOptions && option.PriceOptions.length > 0) ? option.PriceOptions[0] : null;

    if (!selectedPriceOpt) {
      console.error('No PriceOptions available for feedbackId:', feedbackId);
      setCopiedOptionId(feedbackId + '_missing_price_opt');
      setTimeout(() => setCopiedOptionId(null), 3000);
      return;
    }
    console.log('handleCopyBooking: selectedPriceOpt for feedbackId', feedbackId, ':', JSON.stringify(selectedPriceOpt, null, 2));


    // 3. Construct the Base Object
    const dataToCopy = {
      sabrePnrCode: option.itinerary || null,
      fareType: selectedPriceOpt.FareType || "UNKNOWN", // Default if null
      pcc: selectedPriceOpt.PCC || null,
      costs: []
    };

    // 4. Populate costs Array (Removed chargeTypeMap, direct mapping)
    if (selectedPriceOpt.radar_costs && Array.isArray(selectedPriceOpt.radar_costs)) {
      selectedPriceOpt.radar_costs.forEach(cost_item => {
        if (cost_item && cost_item.charge_type && cost_item.charge_type.id !== undefined && cost_item.charge_type.name && cost_item.charge_type.direction) {
          dataToCopy.costs.push({
            rate: cost_item.amount,
            perType: cost_item.cost_per_type,
            chargeType: {
              id: cost_item.charge_type.id,
              name: cost_item.charge_type.name,
              direction: cost_item.charge_type.direction
            },
            chargeTypeId: cost_item.charge_type.id // Assuming chargeTypeId is same as chargeType.id
          });
        } else {
          console.warn('Skipping radar_cost item due to missing or incomplete charge_type:', cost_item);
        }
      });
    }

    // 5. Convert to JSON and Copy
    const jsonStringToCopy = JSON.stringify(dataToCopy, null, 2);
    console.log('handleCopyBooking: JSON data to copy for feedbackId', feedbackId, ':', jsonStringToCopy);

    try {
      await navigator.clipboard.writeText(jsonStringToCopy);
      console.log('Successfully copied JSON to clipboard for feedbackId', feedbackId);
      setCopiedOptionId(feedbackId);
      setTimeout(() => setCopiedOptionId(null), 2000);
    } catch (err) {
      console.error('Failed to copy JSON itinerary for feedbackId', feedbackId, ':', err);
      setCopiedOptionId(feedbackId + '_error'); // General copy error
      setTimeout(() => setCopiedOptionId(null), 2000);
    }
  };

  const formatPrice = (price) => {
    const numPrice = Number(price);
    if (price === null || price === undefined || isNaN(numPrice)) {
      return 'N/A'; // Or some other placeholder like '$--'
    }
    return `$${Math.ceil(numPrice)}`;
  };

  // Helper function to render a single flight segment (layover rendering removed)
  const renderFlightSegment = (flight, flightKey) => {
    const formattedDepartureTime = formatTimeToHHMM(flight.DepartureDateTime);
    const formattedArrivalTime = formatTimeToHHMM(flight.ArrivalDateTime);
    const duration = formatDuration(flight.JourneyDuration);

    // Check if this is an extra-leg (useless) flight
    const isExtraLeg = flight.useless === true;

    // Airline logo rendering function with dimmed styling for extra-legs
    const airlineLogoImg = (airlineCode, size = 24) => (
      <img
        src={`https://d263qmvlt29h99.cloudfront.net/${airlineCode}.svg`}
        alt={`${airlineCode} logo`} 
        style={{ 
          height: `${size}px`, 
          width: `${size}px`, 
          marginRight: '8px', 
          verticalAlign: 'middle',
          opacity: isExtraLeg ? 0.6 : 1,
          filter: isExtraLeg ? 'grayscale(30%)' : 'none'
        }} 
        onError={(e) => { e.target.style.display='none'; }}
      />
    );    
    
    // Ensure BookingClass is safely accessed and rendered
    const bookingClassDisplay = flight.BookingClass && String(flight.BookingClass).trim() !== "" ? ` ${renderSafe(flight.BookingClass)} ${renderSafe(flight.service_class)} ` : "";

    const flightDetailsLine = `${renderSafe(flight.FlightNumber)}${bookingClassDisplay} Departure: ${renderSafe(flight.DepartureAirport)} ${formattedDepartureTime} Arrival: ${renderSafe(flight.ArrivalAirport)} ${formattedArrivalTime} Duration: ${renderSafe(duration)}`;

    // Styling for extra-leg flights
    const segmentStyle = {
      border: isExtraLeg ? '1px dashed #ccc' : '1px solid #eee',
      padding: '10px',
      margin: '5px 0',
      backgroundColor: isExtraLeg ? '#f8f8f8' : 'white',
      opacity: isExtraLeg ? 0.85 : 1,
      position: 'relative'
    };

    const textStyle = {
      color: isExtraLeg ? '#666' : '#000',
      fontStyle: isExtraLeg ? 'italic' : 'normal'
    };

    return (
      <div key={flightKey} className="segment-flight" style={segmentStyle}>
        {isExtraLeg && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '5px',
            fontSize: '0.7em',
            color: '#999',
            fontWeight: 'bold',
            textTransform: 'uppercase'
          }}>
            Extra Leg
          </span>
        )}
        <p className="flight-info" style={textStyle}>
          {airlineLogoImg(flight.MarketingAirline, 20)}{flightDetailsLine}
        </p>
        {flight.OperatingAirline && flight.OperatingAirline !== flight.MarketingAirline && (
          <p className="flight-info operated-by" style={textStyle}>Operated by: {renderSafe(flight.OperatingAirline)}</p>
        )}
      </div>
    );
  };



  // Helper function to render layover information
  const renderLayoverInfo = (airport, duration, layoverKey, isSelfTransfer = false) => (
    <div key={layoverKey} className="layover-info-between-flights" style={{ textAlign: 'center', margin: '10px 0', padding: '8px', backgroundColor: isSelfTransfer ? '#ffe0b2' :'#f0f0f0', borderRadius: '4px', border: isSelfTransfer ? '1px solid #ffb74d' : '1px solid #ddd' }}>
      <p style={{ margin: 0, color: '#333', fontSize: '0.9em' }}>
        {isSelfTransfer && <strong style={{color: '#c56000'}}>Self-transfer: </strong>}
        Layover at {renderSafe(airport)} for {renderSafe(formatDuration(duration))}
      </p>
    </div>
  );


  const formatDuration = (totalMinutes) => {
    const numericTotalMinutes = Number(totalMinutes);
    if (totalMinutes === null || totalMinutes === undefined || isNaN(numericTotalMinutes) || numericTotalMinutes < 0) {
      return 'N/A';
    }
    const hours = Math.floor(numericTotalMinutes / 60);
    const minutes = numericTotalMinutes % 60;
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  };

  const handleRevalidatePrice = async (group, groupIndex) => {
    const groupId = group.Id || `group-${groupIndex}`;

    // Собираем все flight_option_id из группы
    const flightOptions = group.flight_options || [];
    if (flightOptions.length === 0) {
      console.error('No flight options found in group');
      alert('Cannot revalidate: no flight options available');
      return;
    }

    // Извлекаем ID всех flight options
    const flight_option_ids = flightOptions
      .map(option => option.Id)
      .filter(id => id !== null && id !== undefined);

    if (flight_option_ids.length === 0) {
      console.error('No valid flight option IDs found');
      console.log('Group data:', group);
      alert('Cannot revalidate: no valid flight option IDs');
      return;
    }

    console.log('Revalidating flight options:', flight_option_ids);

    setIsRevalidating(true);
    setRevalidatingGroupId(groupId);

    try {
      const response = await fetch(`${API_HOST}/radar/revalidate_price_options_batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          flight_option_ids: flight_option_ids
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Batch revalidation response:', data);

      setRevalidationData(data);
      setIsRevalidationModalOpen(true);
    } catch (error) {
      console.error('Error revalidating price:', error);
      alert(`Error revalidating price: ${error.message}`);
    } finally {
      setIsRevalidating(false);
      setRevalidatingGroupId(null);
    }
  };

  return (
    <div className="trip-order">
      {isLoading && <div className="loading-indicator">Loading...</div>}
      <SearchForm
        userRequest={tripOrder.user_request}
        setTripOrder={setTripOrder}
        handleTotalSearch={handleTotalSearch}
      />

      {/* Google Flights Results */}
      <GoogleFlights
        flights={googleFlights}
        isLoading={isGoogleFlightsLoading}
      />

      {/* Render the Matrix component here, passing the tripOrder.id and filter handler */}
      {tripOrder.id && (
        <>
          <Matrix
            tripOrderId={tripOrder.id}
            onApplyFilter={handleApplyMatrixFilter}
            activeFilters={matrixFilters}
            isResultReadyForMatrix={isResultReadyForMatrix}
          />
          {/* Filter Controls */}
          <div className="filter-controls" style={{
            padding: '15px',
            backgroundColor: '#f5f5f5',
            borderRadius: '5px',
            marginBottom: '20px',
            display: 'flex',
            gap: '20px',
            alignItems: 'center'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showSelfTransfer}
                onChange={(e) => setShowSelfTransfer(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span>Show Self-Transfer</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showExtraLeg}
                onChange={(e) => setShowExtraLeg(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span>Show Extra Leg</span>
            </label>
          </div>
        </>
      )}

      {/* New rendering logic for flight_option_groups (stored in tripOrder.trips) */}
      <div className="flight-option-groups-container">
        {tripOrder.trips && Array.isArray(tripOrder.trips) && tripOrder.trips.map((group, groupIndex) => {
          const groupId = group.Id || `group-${groupIndex}`;
          const currentActiveTab = activeTabs[groupId] || "All Flights";

          // Helper function to generate tab ID for flight options
          const getFlightOptionTabId = (option, index) => option.Id || `option-${index}`;

          return (
            <div key={groupId} className="flight-option-group-card">
              {/* Removed group-journey-details block */}
              <div className="group-total-info" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}> {/* Added margin for spacing */}
                <div>
                  <p style={{ fontSize: '1.2em', margin: '0 0 10px 0' }}><strong>Total Price:</strong> {renderSafe(formatPrice(group.TotalPrice))}</p>
                  <p style={{ margin: 0 }}><strong>Total Duration:</strong> {renderSafe(group.TotalDuration)}</p>
                </div>
                <button
                  onClick={() => handleRevalidatePrice(group, groupIndex)}
                  disabled={isRevalidating && revalidatingGroupId === groupId}
                  style={{
                    padding: '8px 20px',
                    backgroundColor: (isRevalidating && revalidatingGroupId === groupId) ? '#ccc' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (isRevalidating && revalidatingGroupId === groupId) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'background-color 0.2s',
                    minWidth: '100px'
                  }}
                  onMouseEnter={(e) => {
                    if (!(isRevalidating && revalidatingGroupId === groupId)) {
                      e.target.style.backgroundColor = '#1976D2';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(isRevalidating && revalidatingGroupId === groupId)) {
                      e.target.style.backgroundColor = '#2196F3';
                    }
                  }}
                >
                  {(isRevalidating && revalidatingGroupId === groupId) ? 'Updating...' : 'Update'}
                </button>
              </div>

              {/* Tabs Implementation */}
              <div className="tabs-container" style={{ display: 'flex', marginBottom: '1rem', borderBottom: '1px solid #ccc' }}>
                {/* "All Flights" Tab */}
                <div
                  className={`tab ${currentActiveTab === "All Flights" ? "active-tab" : ""}`}
                  onClick={() => handleTabClick(groupId, "All Flights")}
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    border: currentActiveTab === "All Flights" ? '1px solid #ccc' : '1px solid transparent',
                    borderBottom: 'none',
                    backgroundColor: currentActiveTab === "All Flights" ? 'white' : '#f0f0f0',
                    marginRight: '5px',
                    borderTopLeftRadius: '5px',
                    borderTopRightRadius: '5px',
                  }}
                >
                  All Flights
                </div>

                {/* Flight Option Tabs */}
                {group.flight_options && Array.isArray(group.flight_options) && group.flight_options.map((option, optionIndex) => {
                  const optionTabId = getFlightOptionTabId(option, optionIndex);
                  const isActive = currentActiveTab === optionTabId;
                  const firstLeg = option.Legs && option.Legs[0];
                  const tabTitleLine1 = firstLeg ? `${option.AirLine || ''} ${firstLeg.DepartureAirport || ''} -> ${firstLeg.ArrivalAirport || ''}`.trim() : `${option.AirLine || 'N/A'}`;

                  // Safely access BookingClass and PCC
                  const priceOption = option.PriceOptions && option.PriceOptions[0];
                  const bookingClass = priceOption && priceOption.BookingClass ? priceOption.BookingClass : 'N/A';
                  const pccCode = priceOption && priceOption.PCC ? priceOption.PCC : 'N/A';

                  const tabTitleLine2 = `${formatPrice(option.TotalPrice)} [${bookingClass}]  ${pccCode}`;

                  return (
                    <div
                      key={optionTabId}
                      className={`tab ${isActive ? "active-tab" : ""}`}
                      onClick={() => handleTabClick(groupId, optionTabId)}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        border: isActive ? '1px solid #ccc' : '1px solid transparent',
                        borderBottom: 'none',
                        backgroundColor: isActive ? 'white' : '#f0f0f0',
                        marginRight: '5px',
                        borderTopLeftRadius: '5px',
                        borderTopRightRadius: '5px',
                        textAlign: 'center' // Center text for multi-line
                      }}
                    >
                      <div>{tabTitleLine1}</div>
                      <div>{tabTitleLine2}</div>
                      {option.itinerary && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent tab click when button is clicked
                            handleCopyBooking(option, groupIndex, optionIndex);
                          }}
                          className="copy-booking-button" // Will be styled later or use existing styles
                          style={{ marginTop: '5px', padding: '3px 8px', fontSize: '0.8em' }} // Basic styling
                        >
                          {
                            (() => {
                              const currentFeedbackId = option.Id || `gen_${groupIndex}_${optionIndex}`;
                              if (copiedOptionId === currentFeedbackId) return 'Copied!';
                              if (copiedOptionId === currentFeedbackId + '_clipboard_unavailable') return 'Error: Clipboard N/A';
                              if (copiedOptionId === currentFeedbackId + '_missing_price_opt') return 'Error: Price Data Missing';
                              if (copiedOptionId === currentFeedbackId + '_error') return 'Error!';
                              return 'Copy'; // Shortened text
                            })()
                          }
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Tab Content Area */}
              <div className="tab-content" style={{ padding: '1rem', border: '1px solid #ccc', borderTop: 'none' }}>
                {currentActiveTab === "All Flights" && (() => {
                  return (
                    <div>
                      <p><strong>Total Duration:</strong> {formatDuration(group.TotalDuration)}</p>
                      {group.Legs && group.Legs.map((leg, legIndex) => (
                        <React.Fragment key={`leg-${legIndex}`}>
                          {legIndex === 0 && leg.Flights && leg.Flights.length > 0 && <h4>Outbound</h4>}
                          {legIndex === 1 && leg.Flights && leg.Flights.length > 0 && <h4>Inbound</h4>}
                          {leg.DurationMinutes && (
                            <p><strong>Total {legIndex === 0 ? 'Outbound' : 'Inbound'} Duration:</strong> {formatDuration(leg.DurationMinutes)}</p>
                          )}
                          {leg.Flights && leg.Flights.map((flight, flightIndex, arr) => {
                            const nextFlight = arr[flightIndex + 1];
                            let isSelfTransfer = false;
                            
                            // Check for self-transfer between flights using flight_option_id
                            if (nextFlight && flight.flight_option_id !== nextFlight.flight_option_id) {
                              isSelfTransfer = true;
                            }

                            return (
                              <React.Fragment key={`leg-${legIndex}-flight-${flightIndex}`}>
                                {renderFlightSegment(flight, `leg-${legIndex}-flight-${flightIndex}`)}
                                {flight.layover > 0 && flightIndex < arr.length - 1 &&
                                  renderLayoverInfo(flight.ArrivalAirport, flight.layover, `leg-${legIndex}-flight-${flightIndex}-layover`, isSelfTransfer)}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      {(!group.Legs || group.Legs.length === 0) && (
                        <p>No flights available for this group under "All Flights" view.</p>
                      )}
                    </div>
                  );
                })()}
                {group.flight_options && Array.isArray(group.flight_options) && group.flight_options.map((option, optionIndex) => {
                  const optionTabId = getFlightOptionTabId(option, optionIndex);
                  if (currentActiveTab === optionTabId) {
                    return (
                      <div key={optionTabId}>
                        {option.Legs && option.Legs.map((leg, legIdx) => (
                          <React.Fragment key={`${option.Id}-leg-${legIdx}-frag`}>
                            {legIdx === 0 && leg.Flights && leg.Flights.length > 0 && <h4>Outbound</h4>}
                            {legIdx === 1 && leg.Flights && leg.Flights.length > 0 && <h4>Inbound</h4>}
                            {leg.Flights && leg.Flights.map((flight, flightIdx, arr) => (
                              <React.Fragment key={`${option.Id}-leg${legIdx}-flight-${flightIdx}-frag`}>
                                {renderFlightSegment(flight, `${option.Id}-leg${legIdx}-flight-${flightIdx}`)}
                                {flight.layover > 0 && flightIdx < arr.length - 1 &&
                                  renderLayoverInfo(flight.ArrivalAirport, flight.layover, `${option.Id}-leg${legIdx}-flight-${flightIdx}-layover`, false)}
                              </React.Fragment>
                            ))}
                            {(!leg.Flights || leg.Flights.length === 0) && legIdx < 2 && (
                                <p>No flights for this leg.</p> // Only show for first two legs if they are empty
                            )}
                          </React.Fragment>
                        ))}
                        {(!option.Legs || option.Legs.length === 0) && (
                          <p>No flight details available for this option.</p>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Original flight options rendering - TO BE REPLACED BY TAB CONTENT */}
              {/* <h4 className="flight-options-header">Available Options for this Journey:</h4> */}
              <div className="flight-options-list" style={{ display: 'none' /* Temporarily hide old list */ }}>
                {group.flight_options && Array.isArray(group.flight_options) && group.flight_options.length > 0 ? (
                  group.flight_options.map((option, optionIndex) => (
                    <div key={option.Id || optionIndex} className="flight-option-item">
                      <p>Airline: {renderSafe(option.AirLine)}</p>
                      <p>Price: {renderSafe(formatPrice(option.TotalPrice))}</p>
                      <p>Duration: {renderSafe(option.TotalDuration)}</p>
                      <p>Dates: {renderSafe(new Date(option.StartDate).toLocaleString())} - {renderSafe(new Date(option.EndDate).toLocaleString())}</p>
                      {/* Display PriceOptions */}
                    {option.PriceOptions && Array.isArray(option.PriceOptions) && option.PriceOptions.length > 0 && (
                      <div className="price-options-section">
                        <h5 className="price-options-header">Available Fares:</h5>
                        <div className="price-options-list">
                          {option.PriceOptions.map((priceOpt, priceOptIndex) => (
                            <div key={priceOpt.Id || priceOptIndex} className="price-option-detail">
                              <p>PCC: {renderSafe(priceOpt.PCC)}</p>
                              <p>Fare Type: {renderSafe(priceOpt.FareType)}</p>
                              <p>Price: {renderSafe(formatPrice(priceOpt.TotalPrice))}</p>
                              <p>Source: {renderSafe(priceOpt.FareSource)}</p>
                              {priceOpt.BookingClass && <p>Booking Class: {renderSafe(priceOpt.BookingClass)}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                      {/* Flight Segments Details */}
                      <div className="flight-segments-container">
                        <h5 className="flight-segments-header">Flight Details:</h5>
                        {option.Legs && Array.isArray(option.Legs) && option.Legs.length > 0 ? (
                          option.Legs.map((leg, legIndex) => (
                            <div key={legIndex} className="segment-leg">
                              {leg.Flights && Array.isArray(leg.Flights) && leg.Flights.length > 0 ? (
                                leg.Flights.map((flight, flightIndex) => (
                                  // This inner rendering of flight segments is already covered by renderFlightSegment,
                                  // which is now wrapped with renderSafe.
                                  // No direct renderSafe needed here if renderFlightSegment is used.
                                  // However, the provided diff implies changing this part too if it's *not* using renderFlightSegment.
                                  // Assuming this is a more direct rendering without calling renderFlightSegment:
                                  <div key={flightIndex} className="segment-flight">
                                    <p className="flight-info"><strong>Flight {renderSafe(flight.FlightNumber)}</strong> ({renderSafe(flight.BookingClass || 'N/A')})</p>
                                    <p className="flight-info">Departure: {renderSafe(flight.DepartureAirport)} at {renderSafe(flight.DepartureDate)} {renderSafe(flight.DepartureDateTime)}</p>
                                    <p className="flight-info">Arrival: {renderSafe(flight.ArrivalAirport)} at {renderSafe(flight.ArrivalDate)} {renderSafe(flight.ArrivalDateTime)}</p>
                                    <p className="flight-info">Duration: {renderSafe(formatDuration(flight.JourneyDuration))}</p>
                                    {flight.OperatingAirline && flight.OperatingAirline !== flight.MarketingAirline && (
                                      <p className="flight-info operated-by">{renderSafe(flight.OperatingAirline)}</p>
                                    )}
                                    {flight.layover && flight.layover > 0 && (
                                      <p className="layover-info">Layover at {renderSafe(flight.ArrivalAirport)} for {renderSafe(formatDuration(flight.layover))}</p>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <p>No flights in this leg.</p>
                              )}
                            </div>
                          ))
                        ) : (
                          <p>Flight leg details not available.</p>
                        )}
                      </div>
                      {/* Copy button is now part of the tab label, so it's removed from here */}
                    </div>
                  ))
                ) : (
                  <p>No specific flight options listed for this group.</p>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <PriceRevalidationModal
        isOpen={isRevalidationModalOpen}
        onClose={() => setIsRevalidationModalOpen(false)}
        revalidationData={revalidationData}
      />
    </div>
  );
}

export default TripOrder;
